export const AUTH_BASE = 'https://auth.onedaysample.com';
export const SELF_URL = 'https://site-manager.onedaysample.com';
export const MIN_LEVEL = 12;
const TOKEN_KEY = 'ods_auth_token';

// 카카오 로그인 시작 URL — auth-service에 위임, 성공 시 이 서비스로 ?sso_token= 복귀.
export function kakaoLoginUrl(): string {
  return `${AUTH_BASE}/api/auth/kakao/start?return_url=${encodeURIComponent(SELF_URL)}`;
}

// 카카오 SSO 복귀 흡수: ?sso_token=<JWT> → 토큰 저장 + URL 정리.
// currentUser()가 JWT를 직접 디코드하므로 별도 user fetch 불필요.
export function captureSsoToken(): boolean {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('sso_token');
  if (!t) return false;
  setToken(t);
  params.delete('sso_token');
  const qs = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  return true;
}

export interface AuthUser {
  email: string;
  name?: string;
  level: number;
}

interface JwtPayload extends AuthUser {
  exp?: number;
}

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: AuthUser;
  message?: string;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const pad = '='.repeat((4 - (payload.length % 4)) % 4);
    const b64 = (payload + pad).replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

export function currentUser(): AuthUser | null {
  const token = getToken();
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload) return null;
  if (payload.exp && payload.exp * 1000 < Date.now()) {
    clearToken();
    return null;
  }
  return { email: payload.email, name: payload.name, level: payload.level };
}

export function hasAdminAccess(): boolean {
  const user = currentUser();
  return !!user && user.level >= MIN_LEVEL;
}

export async function loginWithPassword(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${AUTH_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json().catch(() => ({}))) as LoginResponse;
  if (!res.ok) {
    return { success: false, message: data.message ?? `HTTP ${res.status}` };
  }
  return data;
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    window.location.href = '/login';
  }
  return res;
}
